import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Configure,
  useInstantSearch,
  useRefinementList,
  useSearchBox,
} from "react-instantsearch-hooks-web";
import { useNavigate } from "react-router-dom";
import { ArrowUpFromLine, ChevronDown, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import algoliasearch from "algoliasearch/lite";
import { useUser } from "../../../providers/database";
import {
  formatDate,
  getAffiliationLabel,
  getInitials,
  getLocation,
  getMilAffiliation,
  STATUS_ORDER,
  StatusRefinementItem,
} from "./seekerAdminUtils";
import { getCurrentDate } from "./util";

const appId = process.env.REACT_APP_ALGOLIA_APP_ID!;
const apiKey = process.env.REACT_APP_ALGOLIA_API_KEY!;
const algoliaClient = algoliasearch(appId, apiKey);

const BASE_FILTERS = "claims.role:SKR OR claims.role:COMM";

function formatHitForExport(hit: any) {
  return {
    Name: hit.name || "-",
    Email: hit.email || "-",
    Phone: hit.phone || "-",
    Location: getLocation(hit),
    "Mil Affiliation": getAffiliationLabel(hit?.affiliation?.affiliation) || "-",
    "Last Active": formatDate(hit.lastLogIn || hit.lastActive),
    Status: hit.status || "Unknown",
  };
}

function downloadCSV(data: Record<string, string>[], filename: string) {
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map((row) =>
      headers.map((h) => `"${(row[h] || "").replace(/"/g, '""')}"`).join(","),
    ),
  ];
  const csvBlob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(csvBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function fetchAllSeekerRecords(
  query: string,
  facetFilters: string[][],
): Promise<any[]> {
  const index = algoliaClient.initIndex("UserQueries");
  const allHits: any[] = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const res = await index.search(query, {
      filters: BASE_FILTERS,
      facetFilters,
      page,
      hitsPerPage: 1000,
    });
    allHits.push(...res.hits);
    hasMore = res.page + 1 < res.nbPages;
    page += 1;
  }

  return allHits;
}

const SelectionContext = createContext({
  selectedIds: new Set() as Set<string>,
  toggleSelection: (_id: string) => {},
});

const AFFILIATION_BADGE_STYLES: Record<string, string> = {
  "Family Member": "bg-[#faeeda] text-[#854f0b]",
  "Military Spouse": "bg-[#e1f5ee] text-[#0f6e56]",
  "Veteran": "bg-[#e6f1fb] text-[#1a73b5]",
  "Active Duty": "bg-[#EEEDFE] text-[#534AB7]",
  "Military Retired": "bg-[#e8dff5] text-[#5b3f8f]",
};

const AffiliationBadge = ({ label }: { label: string }) => {
  if (!label || label === "-") return <span>-</span>;
  const style = AFFILIATION_BADGE_STYLES[label] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap ${style}`}>
      {label}
    </span>
  );
};

const StatusDot = ({ status }: { status?: string }) => {
  const normalized = (status || "").toLowerCase();
  const colorClass =
    normalized === "active"
      ? "bg-instant-teams-green-D1"
      : normalized === "pending"
      ? "bg-yellow-400"
      : "bg-gray-400";

  return (
    <span className="inline-flex items-center gap-2 capitalize">
      <span className={`h-2 w-2 rounded-full ${colorClass}`} />
      {status || "Unknown"}
    </span>
  );
};

function SeekersHeader() {
  const { results } = useInstantSearch();
  return (
    <div className="mb-4 flex items-baseline gap-2">
      <h1 className="text-2xl font-semibold text-instant-teams-blue-Main">Seekers</h1>
      <span className="text-sm text-gray-500">{results?.nbHits || 0} total</span>
    </div>
  );
}

function SeekerFiltersBar() {
  const { query, refine: refineSearch } = useSearchBox();
  const { items, refine: refineStatus } = useRefinementList({
    attribute: "status",
    operator: "or",
    limit: 20,
  });

  const { items: stateItems, refine: refineState } = useRefinementList({
    attribute: "address.state",
    operator: "or",
    limit: 500,
  });

  const { items: affiliationItems, refine: refineAffiliation } = useRefinementList({
    attribute: "affiliation.affiliation",
    operator: "or",
    limit: 50,
  });

  const [open, setOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [affiliationOpen, setAffiliationOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const dropdownRef = useRef(null as HTMLDivElement | null);
  const stateDropdownRef = useRef(null as HTMLDivElement | null);
  const affiliationDropdownRef = useRef(null as HTMLDivElement | null);

  const sortedItems = useMemo(() => {
    const typedItems = items as unknown as StatusRefinementItem[];
    return [...typedItems].sort((a, b) => {
      const ai = STATUS_ORDER.indexOf(String(a.value).toLowerCase());
      const bi = STATUS_ORDER.indexOf(String(b.value).toLowerCase());
      const aOrder = ai === -1 ? 999 : ai;
      const bOrder = bi === -1 ? 999 : bi;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.label).localeCompare(String(b.label));
    });
  }, [items]);

  const moveToLastLabels = ["Outside-USA", "No-Location"];

  const sortedStateItems = useMemo(() => {
    const filtered = stateSearch
      ? stateItems.filter((item) =>
          item.label.toLowerCase().includes(stateSearch.toLowerCase())
        )
      : stateItems;

    return [...filtered].sort((a, b) => {
      const aIsSpecial = moveToLastLabels.includes(a.label);
      const bIsSpecial = moveToLastLabels.includes(b.label);
      if (aIsSpecial && bIsSpecial) return 0;
      if (aIsSpecial) return 1;
      if (bIsSpecial) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [stateItems, stateSearch]);

  const selectedItems = useMemo(
    () => sortedItems.filter((item: StatusRefinementItem) => item.isRefined),
    [sortedItems]
  );

  const selectedStateItems = useMemo(
    () => stateItems.filter((item) => item.isRefined),
    [stateItems]
  );

  const sortedAffiliationItems = useMemo(() => {
    return [...affiliationItems].sort((a, b) => a.label.localeCompare(b.label));
  }, [affiliationItems]);

  const selectedAffiliationItems = useMemo(
    () => affiliationItems.filter((item) => item.isRefined),
    [affiliationItems]
  );

  const hasSelection = selectedItems.length > 0;
  const hasStateSelection = selectedStateItems.length > 0;
  const hasAffiliationSelection = selectedAffiliationItems.length > 0;
  const triggerActive = open || hasSelection;
  const stateTriggerActive = stateOpen || hasStateSelection;
  const affiliationTriggerActive = affiliationOpen || hasAffiliationSelection;
  const buttonLabel = hasSelection
    ? `Status: ${selectedItems.map((i: StatusRefinementItem) => i.label).join(", ")}`
    : "Status";
  const stateButtonLabel = hasStateSelection
    ? `State: ${selectedStateItems.map((i: StatusRefinementItem) => i.label).join(", ")}`
    : "State";
  const affiliationButtonLabel = hasAffiliationSelection
    ? `Affiliation: ${selectedAffiliationItems.map((i: StatusRefinementItem) => getAffiliationLabel(i.label)).join(", ")}`
    : "Affiliation";

  useEffect(() => {
    if (!open && !stateOpen && !affiliationOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      if (open && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
      if (stateOpen && stateDropdownRef.current && !stateDropdownRef.current.contains(e.target as Node)) {
        setStateOpen(false);
        setStateSearch("");
      }
      if (affiliationOpen && affiliationDropdownRef.current && !affiliationDropdownRef.current.contains(e.target as Node)) {
        setAffiliationOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setStateOpen(false);
        setAffiliationOpen(false);
        setStateSearch("");
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, stateOpen, affiliationOpen]);

  const hasAnyChips = hasSelection || hasStateSelection || hasAffiliationSelection;

  return (
    <div className="grid grid-cols-[320px_1fr] items-start gap-3">
      <div className="flex flex-col">
        <div className="relative w-[320px]">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => refineSearch(e.currentTarget.value)}
            placeholder="Search by name, email, or phone..."
            className="h-10 w-full rounded border bg-gray-50 px-10 text-sm focus:outline-none"
          />
        </div>

        {hasAnyChips ? (
          <div className="mb-[-10px] mt-4 flex flex-wrap gap-2">
            {selectedItems.map((item: StatusRefinementItem) => (
              <button
                key={`status-${item.value}`}
                type="button"
                aria-label={`Remove ${item.label} status filter`}
                onClick={() => refineStatus(item.value)}
                className="inline-flex items-center gap-2 rounded-full border-[#155d94] bg-[#e6f1fb] px-3 py-[6px] text-sm text-[#155d94]"
              >
                <span className="flex items-center gap-2 text-[#155d94]">
                  {item.label[0].toUpperCase() + item.label.slice(1)}
                  <span className="text-[#155d94]">{item.count}</span>
                </span>
                <X className="h-3.5 w-3.5 text-[#94afc8]" aria-hidden />
              </button>
            ))}
            {selectedStateItems.map((item: StatusRefinementItem) => (
              <button
                key={`state-${item.value}`}
                type="button"
                aria-label={`Remove ${item.label} state filter`}
                onClick={() => refineState(item.value)}
                className="inline-flex items-center gap-2 rounded-full border-[#155d94] bg-[#e6f1fb]  px-3 py-[6px] text-sm text-[#155d94]"
              >
                <span className="flex items-center gap-2 text-[#155d94]">
                  {item.label}
                  <span className="text-[#155d94]">{item.count}</span>
                </span>
                <X className="h-3.5 w-3.5 text-[#94afc8]" aria-hidden />
              </button>
            ))}
            {selectedAffiliationItems.map((item: StatusRefinementItem) => (
              <button
                key={`affiliation-${item.value}`}
                type="button"
                aria-label={`Remove ${getAffiliationLabel(item.label)} affiliation filter`}
                onClick={() => refineAffiliation(item.value)}
                className="inline-flex items-center gap-2 rounded-full border-[#155d94] bg-[#e6f1fb] px-3 py-[6px] text-sm text-[#155d94]"
              >
                <span className="flex items-center gap-2 text-[#155d94]">
                  {getAffiliationLabel(item.label)}
                  <span className="text-[#155d94]">{item.count}</span>
                </span>
                <X className="h-3.5 w-3.5 text-[#94afc8]" aria-hidden />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex items-start gap-3">
        {/* Status filter */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            aria-expanded={open}
            aria-haspopup="listbox"
            onClick={() => setOpen((v: boolean) => !v)}
            className={`flex h-10 min-w-[180px] max-w-[320px] items-center justify-between gap-2 rounded border px-3 text-left text-sm capitalize transition-colors ${
              triggerActive
                ? "border-[#155d94] bg-[#e6f1fb] text-[#155d94]"
                : "border-gray-300 bg-white text-gray-800"
            }`}
          >
            <span className="truncate">{buttonLabel}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform ${
                open ? "rotate-180" : ""
              } ${triggerActive ? "text-instant-teams-blue-Main" : "text-gray-400"}`}
              aria-hidden
            />
          </button>

          {open ? (
            <div
              className="absolute left-0 top-full z-50 mt-2 max-h-72 w-[240px] overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              role="listbox"
              aria-multiselectable
            >
              {sortedItems.map((item: StatusRefinementItem) => {
                return (
                  <label
                    key={item.value}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm capitalize hover:bg-gray-50"
                    onClick={() => refineStatus(item.value)}
                  >
                    <input
                      type="checkbox"
                      checked={item.isRefined}
                      readOnly
                      className="h-4 w-4 rounded border-gray-300 text-instant-teams-blue-Main focus:ring-instant-teams-blue-Main"
                    />
                    <span className="flex-1">{item.label}</span>
                    <span className="tabular-nums text-xs text-gray-400">{item.count}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* State filter */}
        <div ref={stateDropdownRef} className="relative">
          <button
            type="button"
            aria-expanded={stateOpen}
            aria-haspopup="listbox"
            onClick={() => setStateOpen((v: boolean) => !v)}
            className={`flex h-10 min-w-[180px] max-w-[320px] items-center justify-between gap-2 rounded border px-3 text-left text-sm transition-colors ${
              stateTriggerActive
                ? "border-[#155d94] bg-[#e6f1fb] text-[#155d94]"
                : "border-gray-300 bg-white text-gray-800"
            }`}
          >
            <span className="truncate">{stateButtonLabel}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform ${
                stateOpen ? "rotate-180" : ""
              } ${stateTriggerActive ? "text-instant-teams-blue-Main" : "text-gray-400"}`}
              aria-hidden
            />
          </button>

          {stateOpen ? (
            <div
              className="absolute left-0 top-full z-50 mt-2 w-[260px] rounded-md border border-gray-200 bg-white shadow-lg"
              role="listbox"
              aria-multiselectable
            >
              <div className="border-b px-3 py-2">
                <input
                  type="text"
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  placeholder="Search states..."
                  className="h-8 w-full rounded border bg-gray-50 px-3 text-sm focus:outline-none"
                  autoFocus
                />
              </div>
              <div className="max-h-64 overflow-auto py-1">
                {sortedStateItems.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-gray-400">No states found</p>
                ) : (
                  sortedStateItems.map((item: StatusRefinementItem) => (
                    <label
                      key={item.value}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                      onClick={() => refineState(item.value)}
                    >
                      <input
                        type="checkbox"
                        checked={item.isRefined}
                        readOnly
                        className="h-4 w-4 rounded border-gray-300 text-instant-teams-blue-Main focus:ring-instant-teams-blue-Main"
                      />
                      <span className="flex-1">{item.label}</span>
                      <span className="tabular-nums text-xs text-gray-400">{item.count}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Affiliation filter */}
        <div ref={affiliationDropdownRef} className="relative">
          <button
            type="button"
            aria-expanded={affiliationOpen}
            aria-haspopup="listbox"
            onClick={() => setAffiliationOpen((v: boolean) => !v)}
            className={`flex h-10 min-w-[180px] max-w-[320px] items-center justify-between gap-2 rounded border px-3 text-left text-sm transition-colors ${
              affiliationTriggerActive
                ? "border-[#155d94] bg-[#e6f1fb] text-[#155d94]"
                : "border-gray-300 bg-white text-gray-800"
            }`}
          >
            <span className="truncate">{affiliationButtonLabel}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform ${
                affiliationOpen ? "rotate-180" : ""
              } ${affiliationTriggerActive ? "text-instant-teams-blue-Main" : "text-gray-400"}`}
              aria-hidden
            />
          </button>

          {affiliationOpen ? (
            <div
              className="absolute left-0 top-full z-50 mt-2 max-h-72 w-[240px] overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              role="listbox"
              aria-multiselectable
            >
              {sortedAffiliationItems.map((item: StatusRefinementItem) => (
                <label
                  key={item.value}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                  onClick={() => refineAffiliation(item.value)}
                >
                  <input
                    type="checkbox"
                    checked={item.isRefined}
                    readOnly
                    className="h-4 w-4 rounded border-gray-300 text-instant-teams-blue-Main focus:ring-instant-teams-blue-Main"
                  />
                  <span className="flex-1">{getAffiliationLabel(item.label)}</span>
                  <span className="tabular-nums text-xs text-gray-400">{item.count}</span>
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SeekerRow({ hit }: { hit: any }) {
  const navigate = useNavigate();
  const { data: userData }:any = useUser(hit?.objectID);
  const typedUserData = userData as any;
  const { selectedIds, toggleSelection } = useContext(SelectionContext);
  const isSelected = selectedIds.has(hit?.objectID);

  return (
    <div
      className={`grid cursor-pointer grid-cols-[40px_repeat(7,1fr)] items-center gap-4 border-b border-gray-100 px-4 py-3 text-sm ${
        isSelected ? "bg-blue-50" : ""
      }`}
      onClick={() => navigate(`/seekers/${hit?.objectID}`)}
    >
      <div
        className="flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelection(hit?.objectID)}
          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-instant-teams-blue-Main focus:ring-instant-teams-blue-Main"
        />
      </div>
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">
          {typedUserData?.photoURL ? (
            <img
              src={userData?.photoURL}
              alt={typedUserData?.name}
              className="h-8 w-8 rounded-full"
            />
          ) : (
            getInitials(typedUserData?.name)
          )}
        </div>
        <div className="truncate font-medium">{hit?.name || "-"}</div>
      </div>
      <div className="truncate text-gray-700">{hit?.email || "-"}</div>
      <div className="truncate text-gray-700">{hit?.phone || "-"}</div>
      <div className="truncate text-gray-700">{getLocation(hit)}</div>
      <div className="truncate">
        <AffiliationBadge label={getAffiliationLabel(hit?.affiliation?.affiliation) || "-"} />
      </div>
      <div className="truncate text-gray-700">{formatDate(hit?.lastLogIn || hit?.lastActive)}</div>
      <div className="truncate text-gray-700">
        <StatusDot status={hit?.status} />
      </div>
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function AdminSeekerTab() {
  const { results, indexUiState } = useInstantSearch();
  const hits = (results?.hits || []) as any[];
  const [selectedIds, setSelectedIds] = useState(new Set() as Set<string>);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [exporting, setExporting] = useState(false);

  const sortedHits = useMemo(() => {
    return [...hits].sort((a, b) => {
      const aActive = (a.status || "").toLowerCase() === "active" ? 0 : 1;
      const bActive = (b.status || "").toLowerCase() === "active" ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [hits]);

  const totalPages = Math.max(1, Math.ceil(sortedHits.length / pageSize));
  const safePage = Math.min(currentPage, totalPages - 1);

  const paginatedHits = useMemo(() => {
    const start = safePage * pageSize;
    return sortedHits.slice(start, start + pageSize);
  }, [sortedHits, safePage, pageSize]);

  const hitIds = useMemo(() => hits.map((h) => h.objectID).join(","), [hits]);
  useEffect(() => {
    setSelectedIds(new Set());
    setCurrentPage(0);
  }, [hitIds]);

  useEffect(() => {
    setCurrentPage(0);
  }, [pageSize]);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev: Set<string>) => {
      const allIds = paginatedHits.map((h: any) => h.objectID);
      if (allIds.length > 0 && allIds.every((id: string) => prev.has(id))) {
        return new Set();
      }
      return new Set(allIds);
    });
  }, [paginatedHits]);

  const allSelected = paginatedHits.length > 0 && paginatedHits.every((h: any) => selectedIds.has(h.objectID));
  const someSelected = paginatedHits.some((h: any) => selectedIds.has(h.objectID));

  const selectedData = useMemo(() => {
    return sortedHits
      .filter((hit: any) => selectedIds.has(hit.objectID))
      .map(formatHitForExport);
  }, [sortedHits, selectedIds]);

  const handleExportSelected = useCallback(() => {
    if (selectedData.length === 0) return;
    downloadCSV(selectedData, `seekers_data_selected_${getCurrentDate()}.csv`)
  }, [selectedData]);

  const handleExportAll = useCallback(async () => {
    setExporting(true);
    try {
    
      const query = indexUiState?.query || "";
      const facetFilters: string[][] = [];
      const refinements = (indexUiState as any)?.refinementList || {};
      for (const [attribute, values] of Object.entries(refinements)) {
        if (Array.isArray(values) && values.length > 0) {
          facetFilters.push(values.map((v: string) => `${attribute}:${v}`));
        }
      }
      const allHits = await fetchAllSeekerRecords(query, facetFilters);
      const exportData = allHits.map(formatHitForExport);
      console.log("exportData", exportData);
      downloadCSV(exportData, `seekers_data_all_${getCurrentDate()}.csv`);
    } catch (error) {
      console.error("Export all failed:", error);
    } finally {
      setExporting(false);
    }
  }, [indexUiState]);

  const paginationRange = useMemo(() => {
    const padding = 1;
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(0);
    const start = Math.max(1, safePage - padding);
    const end = Math.min(totalPages - 2, safePage + padding);
    if (start > 1) pages.push("ellipsis");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages - 1);
    return pages;
  }, [totalPages, safePage]);

  return (
    <SelectionContext.Provider value={{ selectedIds, toggleSelection }}>
      <div className="space-y-4 rounded-xl border-[1px] border-gray-300 bg-white">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <SeekersHeader />
            <div className="flex items-center gap-3">
              <div className="pr-3 text-sm">Results per page:</div>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-8 w-20 rounded border px-2 py-0 text-xs"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              {selectedData.length > 0 && (
                <button
                  type="button"
                  onClick={handleExportSelected}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                >
                  <ArrowUpFromLine className="h-4 w-4" />
                  Export Selected ({selectedData.length})
                </button>
              )}
              <button
                type="button"
                onClick={handleExportAll}
                disabled={exporting}
                className="inline-flex items-center gap-2 rounded-md border border-instant-teams-blue-Main bg-instant-teams-blue-Main px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0d4f80] disabled:opacity-60"
              >
                <ArrowUpFromLine className="h-4 w-4" />
                {exporting ? "Exporting..." : `Export All (${results?.nbHits || 0})`}
              </button>
            </div>
          </div>

          <SeekerFiltersBar />
        </div>

        <div className="overflow-hidden border bg-white">
          <div className="grid grid-cols-[40px_repeat(7,1fr)] gap-4 border-b bg-gray-50 px-4 py-3 text-xs font-semibold uppercase text-gray-500">
            <div className="flex items-center justify-center">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={toggleSelectAll}
                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-instant-teams-blue-Main focus:ring-instant-teams-blue-Main"
              />
            </div>
            <div>Name</div>
            <div>Email</div>
            <div>Phone</div>
            <div>Location</div>
            <div>Mil. Affiliation</div>
            <div>Last Active</div>
            <div>Status</div>
          </div>

          {/* @ts-ignore */}
          <Configure filters="claims.role:SKR OR claims.role:COMM" hitsPerPage={10000} />
          <div>
            {paginatedHits.map((hit: any) => (
              <SeekerRow key={hit.objectID} hit={hit} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-1 pb-6 pt-4">
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setCurrentPage((p: number) => Math.max(0, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded border bg-white text-sm disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {paginationRange.map((item: number | "ellipsis", idx: number) =>
            item === "ellipsis" ? (
              <span key={`ellipsis-${idx}`} className="flex h-8 w-8 items-center justify-center text-sm text-gray-400">
                &hellip;
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => setCurrentPage(item)}
                className={`flex h-8 w-8 items-center justify-center rounded border text-sm ${
                  item === safePage ? "scale-105 border-black font-semibold" : "bg-white"
                }`}
              >
                {item + 1}
              </button>
            )
          )}
          <button
            type="button"
            disabled={safePage >= totalPages - 1}
            onClick={() => setCurrentPage((p: number) => Math.min(totalPages - 1, p + 1))}
            className="flex h-8 w-8 items-center justify-center rounded border bg-white text-sm disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </SelectionContext.Provider>
  );
}
